// =============================================================
// types.ts — SDK 공용 타입
//  핵심: WalletAdapter 인터페이스. injected/Reown 어느 쪽이든
//  이 인터페이스만 만족하면 나머지 SDK(ChainClient/SwapService)는 동일.
// =============================================================

import type { BrowserProvider } from 'ethers';

// tx 상태머신: idle → ready → signing → pending → confirming → success/failed
export const TxState = {
  Idle: 'idle',
  Signing: 'signing',
  Pending: 'pending',
  Confirming: 'confirming',
  Success: 'success',
  Failed: 'failed',
} as const;
export type TxStateValue = (typeof TxState)[keyof typeof TxState];

export interface TxUpdate {
  state: TxStateValue;
  hash?: string;
  molOut?: bigint;
  error?: string;
}

export interface WalletSnapshot {
  connected: boolean;
  address: string | null;
  short: string | null;
  chainOk: boolean;
}

/**
 * WalletAdapter — 지갑 연결 추상화.
 *  injected(window.ethereum) 와 Reown AppKit 을 동일 인터페이스로.
 *  교체 시 main.ts 는 손대지 않는다.
 */
export interface WalletAdapter {
  /** 연결 시도. 성공 시 주소 반환 */
  connect(): Promise<string>;
  /** 페이지 로드 시 기존 세션이 있으면 조용히 복원 (없으면 no-op) */
  restore(): Promise<void>;
  disconnect(): Promise<void>;
  /** ethers BrowserProvider (read/write 공용) */
  getProvider(): Promise<BrowserProvider>;
  /** 현재 주소 (소문자) 또는 null */
  getAddress(): string | null;
  /** 대상 체인 보장 (전환/추가) */
  ensureChain(): Promise<void>;
  /** 상태 변화 구독 (계정/체인 변경) */
  onChange(cb: (snap: WalletSnapshot) => void): () => void;
  /** (선택) 지갑이 응답하지 않을 때(stuck) 알림 구독. injected 어댑터에서 구현. */
  onStuck?(cb: () => void): () => void;
  snapshot(): WalletSnapshot;
}

export interface QuoteResult {
  molOut: bigint;
  baseMol: bigint;
  discountBp: number;
  /** 총판 보상 BNB */
  reward: bigint;
  /** 금고로 가는 BNB */
  toTreasury: bigint;
}

export interface DistributorInfo {
  exists: boolean;
  active: boolean;
  discountBp: number;
  rewardBp: number;
  ownerSet: boolean;
}

export interface PriceInfo {
  /** BNB/USD, 8dec */
  bnbUsd8: bigint;
  /** MOL/USD, 8dec */
  molPriceUsd8: bigint;
  /** 최소 구매 MOL (wei) */
  minMol: bigint;
}

export interface GateInfo {
  stock: bigint;
  myMol: bigint | null;
  paused: boolean;
  bnbUsd8: bigint;
  molPriceUsd8: bigint;
  minMol: bigint;
}

export type SdkErrorCode =
  | 'NO_WALLET'
  | 'USER_REJECTED'
  | 'INSUFFICIENT_BNB'
  | 'NO_GATE_ADDRESS'
  | 'WRONG_CHAIN'
  | 'INSUFFICIENT_MOL_STOCK'
  | 'GATE_CLOSED'
  | 'ZERO_AMOUNT'
  | 'UNKNOWN';

export class SdkError extends Error {
  code: SdkErrorCode;
  constructor(code: SdkErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
    this.name = 'SdkError';
  }
}